import { inject, Injectable } from '@angular/core';
import {
  IConversationSettingsDTO,
  IAgentResponseDTO,
  IAgentCard,
  TTSRequest,
  AudioGenerated,
  ModelName,
  TranscriptionsWhisper,
  ChatRole,
} from '../models/agent.models';
import { FiltersConfig, IFilterQueryResponse, HttpCoreService, ChatUserSettings, IAIModel, EntityCommunicationService } from '@dataclouder/ngx-core';

const Endpoints = {
  // Existing
  GenerateImage: 'api/agent-cards-generate/generate-image-card', // For completeAgentCard, generateImageForCard, generateMainImage
  AgentChat: 'api/agent-cards/chat', // For callChatCompletion, callInstruction
  UpdateAgentCard: 'api/agent-cards/partial-update', // For findById, getAll, save (POST/PUT), delete

  // New
  AgentCard: 'api/agent-cards', // For findById, getAll, save (POST/PUT), delete
  AgentCardQuery: 'api/agent-cards/query', // For filterConversationCards, findAgentCardByTitle
  UserCards: 'api/agent-cards/user-cards', // For findFilteredAgentCards
  VertexTTS: 'api/vertex-adapter/tts/synthesize',
  ListModels: 'api/agent-cards/models', // For getListModels
  TranslateConversation: 'api/agent-cards/translate', // For translateConversation
  TranscribeAudio: 'api/audio/transcribe/whisper', // For getAudioTranscriptions
  // UserChatSettings: 'api/users/chat-settings', // Example for get/saveConversationUserChatSettings if not throwing error
};

// ♦️ Finish this will be the default so i dont need to create a new one everytime
@Injectable({
  providedIn: 'root',
})
export class DefaultAgentCardsService extends EntityCommunicationService<IAgentCard> {
  constructor() {
    super('agent-cards');
  }

  partialUpdateAgentCard(agentCard: IAgentCard): Promise<IAgentCard> {
    return this.httpService.put<IAgentCard>(`${Endpoints.UpdateAgentCard}`, agentCard, 'primary');
  }

  generateMainImage(idCard: string): Promise<any> {
    return this.httpService.get(`${Endpoints.GenerateImage}/${idCard}`, 'primary');
  }

  completeAgentCard(idCard: string): Promise<any> {
    // TODO: i need a default method to do this.
    return this.httpService.get(`${Endpoints.GenerateImage}/${idCard}`, 'primary');
  }

  public async callChatCompletion(conversation: IConversationSettingsDTO): Promise<IAgentResponseDTO> {
    let messages = conversation.messages.map((m) => ({ content: m.content, role: m.role }));
    messages = messages.filter((m) => m.role != ChatRole.AssistantHelper);
    const conversationFiltered = { ...conversation, messages };

    if (conversation.returnJson) {
      return await this.httpService.post<IAgentResponseDTO>(`${Endpoints.AgentChat}?returnJson=true`, conversationFiltered, 'primary');
    } else {
      return await this.httpService.post<IAgentResponseDTO>(`${Endpoints.AgentChat}`, conversationFiltered, 'primary');
    }
  }

  async filterConversationCards(filters: FiltersConfig): Promise<IFilterQueryResponse<IAgentCard>> {
    return this.httpService.post<IFilterQueryResponse<IAgentCard>>(`${Endpoints.AgentCardQuery}`, filters, 'primary');
  }

  async getAllConversationCards(): Promise<IAgentCard[]> {
    return this.httpService.get<IAgentCard[]>(`${Endpoints.AgentCard}`, 'primary');
  }

  async findFilteredAgentCards(paginator: FiltersConfig): Promise<IFilterQueryResponse<IAgentCard>> {
    return this.httpService.post<IFilterQueryResponse<IAgentCard>>(`${Endpoints.UserCards}`, paginator, 'primary');
  }

  async findAgentCardByTitle(title: string): Promise<IAgentCard> {
    const filters: FiltersConfig = { filters: { title } };
    const response = await this.httpService.post<IFilterQueryResponse<IAgentCard>>(`${Endpoints.AgentCardQuery}`, filters, 'primary');
    return response.rows[0];
  }

  override async remove(id: string): Promise<void> {
    // Assuming the backend returns the deleted card, or this matches an expected API contract.
    // HttpCoreService.delete by default returns Promise<DeletedData>.
    await this.httpService.delete<IAgentCard>(`${Endpoints.AgentCard}/${id}`, 'primary');
  }

  async getTextAudioFile(tts: TTSRequest): Promise<AudioGenerated> {
    // Note: Original service in agent-cards.service.ts used 'node' as host.
    // HttpCoreService manages hosts via its configuration or per-call parameter. Using 'primary'.
    const httpResponse = await this.httpService.postFileAndGetBlob(`${Endpoints.VertexTTS}`, tts, 'primary');
    const audioData: AudioGenerated = { blobUrl: '', transcription: null };

    const transcriptionHeader = httpResponse.headers.get('transcription');
    if (transcriptionHeader) {
      try {
        audioData.transcription = JSON.parse(transcriptionHeader);
      } catch (e) {
        console.error('Failed to parse transcription header:', e, transcriptionHeader);
        // Optionally handle or log this error more robustly
      }
    }

    if (httpResponse.body) {
      audioData.blobUrl = window.URL.createObjectURL(httpResponse.body);
    }
    return audioData;
  }

  async getConversationUserChatSettings(): Promise<ChatUserSettings> {
    throw new Error(
      'Method getConversationUserChatSettings cannot be implemented in DefaultAgentCardsService without User  or an alternative settings source.',
    );
  }

  async getListModels(provider: string): Promise<ModelName[]> {
    // Note: Original service in agent-cards.service.ts used 'node' as host. Using 'primary'.
    return this.httpService.get<ModelName[]>(`${Endpoints.ListModels}?provider=${provider}`, 'primary');
  }

  async translateConversation(currentLang: string, targetLang: string, id: string): Promise<ChatUserSettings> {
    // Note: Original service in agent-cards.service.ts used 'python' as host. Using 'primary'.
    // Parameter name in abstract class is 'id', base service used 'idCard'. Using 'id'.
    return this.httpService.post<ChatUserSettings>(
      `${Endpoints.TranslateConversation}`,
      { currentLang, targetLang, idCard: id }, // Sending as idCard as per base service's DTO
      'primary',
    );
  }

  async getAudioTranscriptions(audio: Blob, options: unknown): Promise<TranscriptionsWhisper> {
    // Note: Original service in agent-cards.service.ts used 'node' as host. Using 'primary'.
    // 'options' parameter corresponds to 'metadata' in the original service.
    return this.httpService.uploadFile<TranscriptionsWhisper>(`${Endpoints.TranscribeAudio}`, audio, options, 'primary');
  }

  async callInstruction(prompt: string, model: IAIModel): Promise<IAgentResponseDTO> {
    const messages = [{ content: prompt, role: ChatRole.User }];
    const conversationDto = { messages, model };
    // Note: Original service in agent-cards.service.ts used 'python' as host. Using 'primary'.
    return this.httpService.post<IAgentResponseDTO>(`${Endpoints.AgentChat}`, conversationDto, 'primary');
  }

  async saveConversationUserChatSettings(settings: ChatUserSettings): Promise<ChatUserSettings> {
    throw new Error(
      'Method saveConversationUserChatSettings cannot be implemented in DefaultAgentCardsService without User Service/ToastService or an alternative.',
    );
  }

  generateImageForCard(idCard: string) {
    return this.httpService.get(`${Endpoints.GenerateImage}/${idCard}`, 'primary');
  }
}
